import React, { Fragment, useState, useRef, useEffect } from "react";
import styled, { createGlobalStyle } from "styled-components";
import ThemeProvider from "./components/ThemeProvider";
import { View, Text, Divider, Link } from "./components";
import { Transition, animated, interpolate } from "react-spring";
import { graphql } from "react-apollo";
import { compose } from "recompose";
import { Router, Location, Match, Redirect } from "@reach/router";
import ManageScroll from "./components/ManageScroll";
import albumsQuery from "./graphql/albumsQuery";
import Gallery from "./components/Gallery";
import GalleryPage from "./GalleryPage";
import Logo from "./components/Logo";
import historyLength from "./utilities/historyLength";
import Artists from "./Artists";
import Artist from "./Artist";
import ContactPage from "./ContactPage";
import New from "./New";
import ArtistsIndex from "./ArtistsIndex";
import ProfessionsMenu from "./ProfessionsMenu";
import scrollIntoView from "scroll-into-view-if-needed";
import smoothScrollIntoView from "smooth-scroll-into-view-if-needed";
import Footer from "./Footer";
import useComponentSize from "@rehooks/component-size";
import OverlayMenu, { Consumer as ToggleOverlayMenu } from "./OverlayMenu";
import AnchorsTest from "./Test";
import LogoSmall from "./components/LogoSmall";
import { useMedia } from "use-media";
import MenuIcon from "./components/MenuIcon";
import CloseIcon from "./components/CloseIcon";
import { RotateIcon, MaxHeightContainer } from "./ArtistsIndexMobile";
import { useToggle } from "react-hooks-lib";
import { useLocation } from "react-use";
// import Education from "./Education";
import EducationLanding from "./EducationLanding";
import { Helmet } from "react-helmet";

const onHOOnl =
  window.location.hostname === "houseoforange.nl" ||
  window.location.hostname === "localhost";

const scrollIntoViewSmoothly =
  "scrollBehavior" in document.documentElement.style
    ? scrollIntoView
    : smoothScrollIntoView;

export const scrollDown = () => {
  if (window.pageYOffset < 460) {
    setTimeout(() => {
      const node = window.document.getElementById("anchor");
      if (node) {
        scrollIntoViewSmoothly(node, {
          behavior: "smooth",
          scrollMode: "always",
          block: "start",
          inline: "start",
        });
      }
    }, 300);
  } else {
    //  if (window.pageYOffset > 460)
    const node = window.document.getElementById("anchor2");
    const rect = node && node.getBoundingClientRect();
    if (node) window.scrollTo(0, window.pageYOffset + rect.top - 120);
  }
};

const Container = styled(View)`
  position: relative;
  ${({ theme: { maxWidth } }) => maxWidth};
`;

const ScrollContainer = styled(View).attrs(() => ({ pb: 2 }))`
  display: flex;
  justify-content: space-between;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const NavContainer = styled(View).attrs(() => ({
  mb: [1, 3, 4],
  mt: [1, 0, 0],
}))`
  display: flex;
`;

const LeftSide = styled(View)`
  flex: 1;
  @media (max-width: 480px) {
    flex: none;
    min-width: 52px;
  }
`;

const RightSide = styled(View).attrs(() => ({ pl: 3, as: "nav" }))`
  flex: 1;
  text-transform: uppercase;
  min-width: 470px;
  & ul {
    width: 100%;
    display: flex;
    flex: 1;
    justify-content: space-between;
    list-style-type: none;
    padding: 0;
    margin: 0;
  }
`;

const NavItem = styled(Link).attrs(() => ({ textStyle: "navigation" }))`
  display: block;
`;

const Home = ({ isWide }) => {
  useEffect(() => {
    sessionStorage.setItem("whereWasI", "home");
  }, []);

  return (
    <Fragment>
      {isWide ? <Header /> : <MobileHeader />}
      <New />
      {isWide && (
        <Fragment>
          <Divider mt={6} mb={[2, 2, 2]} />
          <Text textStyle="headline">Artists</Text>
          <Divider mb={[2, 3, 3]} />
          <ArtistsIndex />
        </Fragment>
      )}
      <Footer isWide={isWide} />
    </Fragment>
  );
};

const HeaderContainer = styled(Text)`
  position: sticky;
  top: 0;
  z-index: 30;
  background-color: var(--bg-color);
`;

const Move = styled.div`
  position: relative;
  transition: top 250ms ease;
`;

export const ExpandMenu = ({ items }) => {
  const { on, toggle, reset } = useToggle(false);
  const [justClicked, setJustClicked] = useState(-1);
  const state = useLocation();
  const activeItem = items.findIndex(
    (item) =>
      state.pathname === item.to || (item.active && item.active === true),
  );
  const click = (index) => {
    setJustClicked(index);
    console.log({ index });
    // setTimeout(() => setJustClicked(null), 2000)
  };
  console.log({ justClicked });
  const scrollCorrect =
    justClicked > -1 ? justClicked : Math.max(activeItem, 0);
  return (
    <Fragment>
      <RotateIcon
        icon="arrow"
        size={14}
        style={{ float: "right" }}
        rotate={on}
        mt={2}
        onClick={toggle}
      />
      <MaxHeightContainer
        style={{
          minHeight: 32,
          ...(on && { maxHeight: 4 + items.length * 25.6 }),
        }}
        onClick={() => {
          if (!on) toggle();
        }}
        on={on}
      >
        <Move
          style={{
            top: on ? 0 : scrollCorrect * -25.6,
          }}
        >
          {items.map(({ to, label, ...rest }, index) => (
            <Link
              {...rest}
              invert
              block
              to={to}
              mb={2}
              mt={2}
              onClick={(e) => {
                if (activeItem === index) {
                  e.preventDefault();
                }
                toggle();
                click(index);
                setTimeout(() => setJustClicked(-1), 2000);
              }}
              key={index}
            >
              {label}
            </Link>
          ))}
        </Move>
      </MaxHeightContainer>
    </Fragment>
  );
};

const Sticky = styled(View).attrs(() => ({ bg: "white", pb: 2 }))`
  position: sticky;
  top: 0;
  z-index: 987;
`;

export const MobileHeader = ({
  artists,
  nologo,
  children,
  items,
  fixed = false,
}) => {
  const StickyOrNot = fixed ? Fragment : Sticky;
  return (
    <Location>
      {({ location }) => (
        <Fragment>
          <ToggleOverlayMenu>
            {({ on, toggle }) =>
              on ? (
                <CloseIcon
                  style={{
                    position: "fixed",
                    top: fixed ? 20 : 20,
                    right: 0,
                    left: "auto",
                    zIndex: 999,
                  }}
                  onClick={toggle}
                  mr={[4, 4, 5]}
                />
              ) : (
                <MenuIcon
                  style={{
                    position: "fixed",
                    top: fixed ? 20 : 20,
                    right: 0,
                    left: "auto",
                    zIndex: 999,
                  }}
                  onClick={toggle}
                  mr={[4, 4, 5]}
                />
              )
            }
          </ToggleOverlayMenu>
          <StickyOrNot>
            {!nologo && (
              <View
                style={{
                  ...(location.pathname === "/" && { cursor: "default" }),
                }}
                pt={4}
              >
                <LogoSmall
                  style={{ right: "auto", position: "relative", marginTop: 0 }}
                />
              </View>
            )}
            {children}
            <Divider mt={3} mb={0} />
            <ExpandMenu
              items={
                items
                  ? items
                  : [
                      { to: "/", label: "News" },
                      { to: "/artists", label: "Artists" },
                      { to: "/education", label: "Education" },
                      { to: "/contact", label: "Contact" },
                    ]
              }
            />
            <Divider mt={0} mb={2} />
          </StickyOrNot>
        </Fragment>
      )}
    </Location>
  );
};

const HeaderHeight = createGlobalStyle`
  .header-offset {
    height ${({ height }) => (height / 5) * 2}px;
  }
`;

export const Header = ({ artists, nologo }) => {
  const ref = useRef();
  // const onScreen = useOnScreen(ref)
  let { width, height } = useComponentSize(ref);

  return (
    <Location>
      {({ location }) => (
        <Fragment>
          <HeaderContainer
            mb={nologo ? 0 : 6}
            style={{
              top: nologo ? 16 : -1 * height + height / 13,
              marginTop: nologo ? -8 : 0,
            }}
            pb={4}
          >
            <div ref={ref} style={{ paddingBottom: "calc(3vh + 3vw)" }}>
              {!nologo && (
                <Link
                  to={"/"}
                  style={
                    location.pathname === "/" ? { cursor: "default" } : null
                  }
                  onClick={(e) => {
                    if (!onHOOnl) {
                      e.preventDefault();
                      window.location.href = "https://houseoforange.nl";
                    }
                  }}
                >
                  <Logo
                    width="100%"
                    style={{
                      marginBottom: "-0.5vw",
                      paddingTop: "calc(4vh + 6vw)",
                    }}
                  />
                </Link>
              )}
            </div>
            <HeaderHeight height={height} />
            <div
              id="anchor"
              style={{ height: 0, position: "relative", top: -46 }}
            />
            <NavContainer pt={[1, 0, 0]}>
              <LeftSide>
                <ToggleOverlayMenu>
                  {({ toggle }) => (
                    <MenuIcon style={{ left: 0 }} onClick={toggle} />
                  )}
                </ToggleOverlayMenu>
              </LeftSide>
              <RightSide>
                <ul>
                  <li>
                    <NavItem
                      to="/"
                      isCurrent={location.pathname.length < 2}
                      onClick={(e) => {
                        if (!onHOOnl) {
                          e.preventDefault();
                          window.location.href = "https://houseoforange.nl";
                        } else {
                          scrollDown();
                        }
                      }}
                    >
                      News
                    </NavItem>
                  </li>
                  <li>
                    <NavItem
                      to="/artists"
                      isCurrent={location.pathname.includes("artists")}
                      onClick={(e) => {
                        if (!onHOOnl) {
                          e.preventDefault();
                          window.location.href =
                            "https://houseoforange.nl/artists";
                        } else {
                          scrollDown();
                        }
                      }}
                    >
                      Artists
                    </NavItem>
                  </li>
                  <li>
                    <NavItem
                      to="/education"
                      // isCurrent={location.pathname.includes("education")}
                      // onClick={(e) => {
                      //   if (!onHOOnl) {
                      //     e.preventDefault();
                      //     window.location.href =
                      //       "https://houseoforange.nl/education";
                      //   } else {
                      //     scrollDown();
                      //   }
                      // }}
                      as="a"
                      href="https://www.houseoforangeeducation.nl/"
                      target="_blank"
                    >
                      Education
                    </NavItem>
                  </li>
                  <li>
                    <NavItem
                      to="/contact"
                      isCurrent={location.pathname.includes("contact")}
                      onClick={(e) => {
                        if (!onHOOnl) {
                          e.preventDefault();
                          window.location.href =
                            "https://houseoforange.nl/contact";
                        } else {
                          scrollDown();
                        }
                      }}
                    >
                      Contact
                    </NavItem>
                  </li>
                </ul>
              </RightSide>
            </NavContainer>
            {artists && <ProfessionsMenu />}
            <Divider thick m={0} />
          </HeaderContainer>
          <div
            id="anchor2"
            style={{ height: 0, position: "relative", top: -46 }}
          />
        </Fragment>
      )}
    </Location>
  );
};

const Forward = ({ artist }) => <Redirect to={"artist/" + artist} noThrow />;

const OpenExternal = ({ to }) => {
  useEffect(() => {
    window.location.href = to;
  }, [to]);
  return null;
};

const RedirectTo = ({ to }) =>
  to.startsWith("http") ? (
    <OpenExternal to={to} />
  ) : (
    <Redirect to={to} noThrow />
  );

const App = ({ data }) => {
  const isWide = useMedia({ minWidth: 800 });
  return (
    <ThemeProvider>
      <OverlayMenu>
        <Helmet>
          <title>House Of Orange Agency • Amsterdam</title>
          <meta name="og:title" content="House Of Orange Agency • Amsterdam" />
          <meta
            name="description"
            content="House Of Orange Agency based in Amsterdam serves the international fashion industry, from high end to cutting edge, with it's top talent league of 50 stylists, hair and makeup artists and an ambitious production team for projects running from catwalk shows to campaigns."
          />
          <meta
            name="og:description"
            content="House Of Orange Agency based in Amsterdam serves the international fashion industry, from high end to cutting edge, with it's top talent league of 50 stylists, hair and makeup artists and an ambitious production team for projects running from catwalk shows to campaigns."
          />
        </Helmet>
        <Container px={[4, 4, 5]}>
          <Router primary={false}>
            <Home path="/" isWide={isWide} />
            <ContactPage path="/contact" isWide={isWide} />
            {/* <Education path="/education" isWide={isWide} /> */}
            {/* <EducationLanding path="/education-landing" isWide={isWide} /> */}
            <Artists path="/artists" isWide={isWide} />
            <Artists path="/artists/:profession" isWide={isWide} />
            <Artist path="/artist/:artist" isWide={isWide} />
            <Artist path="/artist/:artist/:category" isWide={isWide} />
            <Forward path="/:anything/:artist/*" />
            <RedirectTo path="/site" to="/" />
            <RedirectTo path="/makeupschool/site" to="/" />
            <RedirectTo path="/artists/hair" to="/" />
            <RedirectTo path="/artist/daniel" to="/" />
            <RedirectTo path="/artist/sarah-cloe/editorial/mirthe-1" to="/" />
            <RedirectTo path="/productions" to="/" />
            <RedirectTo path="/about" to="/" />
            <RedirectTo
              path="/education"
              to="https://www.houseoforangeeducation.nl/"
            />
            <RedirectTo
              path="/education-landing"
              to="https://www.houseoforangeeducation.nl/"
            />
            <RedirectTo
              path="/makeup-school"
              to="https://www.houseoforangeeducation.nl/"
            />
            <RedirectTo
              path="/artist/education"
              to="https://www.houseoforangeeducation.nl/"
            />
            <RedirectTo
              path="/education/beeld-2"
              to="https://www.houseoforangeeducation.nl/"
            />
            <RedirectTo
              path="/education/:anything"
              to="https://www.houseoforangeeducation.nl/"
            />

            <GalleryPage path="/artist/:artist/:category/:album" />
            <GalleryPage path="/:artist/:album" education />
            <GalleryPage path="/album/:album" />
            <AnchorsTest path="/test" />

            <RedirectTo path="/*" to="/" />
          </Router>
          <ManageScroll />
        </Container>
      </OverlayMenu>
    </ThemeProvider>
  );
};

/*
const AppWithData = compose(
  graphql(albumsQuery, {
    options: props => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          user: process.env.REACT_APP_WHITESPACE_USER
        }
      }
    }
  })
)(App)
*/

export default App;
